import { Injectable } from '@angular/core';
import { cloneDeep as _cloneDeep, isNil as _isNil } from 'lodash';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';
import { of as RxOf } from 'rxjs/observable/of';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';

import { IAccountGenericSetting } from '@blcloud/bl-datamodel';
import { BlApiErrorInfo, HttpStatus } from '@blcloud/bl-ng-common';
import { AccountGenericSettingResource } from '@blcloud/bl-ng-resource';
import { LoginInfoResourceService, PageId } from '@blcloud/bl-ng-share-module';

import {
  BaseScreenActionSetting,
  DEFAULT_MONTH_WEEK_DAY_DIV_END,
  DEFAULT_MONTH_WEEK_DAY_DIV_START,
  DEFAULT_SEARCH_DATE_RANGE_END_DAYS,
  DEFAULT_SEARCH_DATE_RANGE_START_DAYS,
  DEFAULT_SEARCH_TARGET_DATE_TYPE,
  IReceptSearchScreenActionSetting,
  SCREEN_ACTION_SETTING_ID,
} from './recept-search-screen-action-setting-modal.define';

/**
 * 受付一覧初期表示設定管理サービス
 */
@Injectable()
export class ReceptSearchScreenActionSettingService {
  /** アカウント別汎用設定 */
  private _accountGenericSetting: IAccountGenericSetting;

  /** 受付一覧初期表示設定 */
  private _screenActionSetting: IReceptSearchScreenActionSetting;
  /** 受付一覧初期表示設定 */
  get screenActionSetting(): IReceptSearchScreenActionSetting {
    return this._screenActionSetting;
  }

  /** 受付一覧初期表示設定変更通知Subject */
  private changeSettingSubject = new BehaviorSubject<IReceptSearchScreenActionSetting>(undefined);
  /** 受付一覧初期表示設定変更通知ストリーム */
  get changeSetting$(): Observable<IReceptSearchScreenActionSetting> {
    return this.changeSettingSubject.asObservable();
  }

  constructor(
    private loginInfoResourceService: LoginInfoResourceService,
    private accountGenericSettingResource: AccountGenericSettingResource,
  ) {
  }

  /**
   * 受付一覧初期表示設定を取得します。
   * @returns 受付一覧初期表示設定
   */
  fetch(): Observable<IReceptSearchScreenActionSetting> {
    return this.getAccountGenericSetting().pipe(
      tap(accountGenericSetting => {
        this._accountGenericSetting = accountGenericSetting;
      }),
      map(accountGenericSetting => {
        return this.parseScreenActionSetting(accountGenericSetting);
      }),
      tap(screenActionSetting => {
        this._screenActionSetting = screenActionSetting;
        this.changeSettingSubject.next(screenActionSetting);
      }),
    );
  }

  /**
   * 受付一覧初期表示設定を保存します。
   * @param screenActionSetting 受付一覧初期表示設定
   */
  save(screenActionSetting: IReceptSearchScreenActionSetting): Observable<any> {
    if (this._accountGenericSetting) {
      // 更新
      const entity: IAccountGenericSetting = {
        blTenantId: this._accountGenericSetting.blTenantId,
        blUserId: this._accountGenericSetting.blUserId,
        pageId: this._accountGenericSetting.pageId,
        settingDataId: this._accountGenericSetting.settingDataId,
        settingInfoDataJson: JSON.stringify(screenActionSetting)
      };

      return this.putAccountGenericSetting(entity).pipe(
        tap(() => {
          this._accountGenericSetting = entity;
          this._screenActionSetting = screenActionSetting;
          this.changeSettingSubject.next(screenActionSetting);
        })
      );

    } else {
      // 新規作成
      const entity: IAccountGenericSetting = {
        pageId: PageId.ReceptionCaseList,
        settingDataId: SCREEN_ACTION_SETTING_ID,
        settingInfoDataJson: JSON.stringify(screenActionSetting)
      };

      return this.postAccountGenericSetting(entity).pipe(
        tap(() => {
          this._accountGenericSetting = entity;
          this._screenActionSetting = screenActionSetting;
          this.changeSettingSubject.next(screenActionSetting);
        })
      );
    }
  }

  /**
   * blUserId取得
   */
  private getBlUserId(): Observable<string> {
    return this.loginInfoResourceService.getLoginInfo().pipe(
      map(loginInfo => loginInfo.blUserId)
    );
  }

  /**
   * アカウント別汎用設定を取得します
   * @returns アカウント別汎用設定
   */
  private getAccountGenericSetting(): Observable<IAccountGenericSetting> {
    const pageId = PageId.ReceptionCaseList;
    const settingDataId = SCREEN_ACTION_SETTING_ID;

    return this.getBlUserId().pipe(
      mergeMap(blUserId => {
        const id = this.accountGenericSettingResource.getIdString({
          blUserId,
          pageId,
          settingDataId
        });
        return this.accountGenericSettingResource.getById(id);
      }),
      catchError((error: BlApiErrorInfo) => {
        if (error.status === HttpStatus.NotFound) {
          return RxOf(null);
        } else {
          throw error;
        }
      })
    );
  }

  /**
   * アカウント別汎用設定を更新します。
   * @param setting アカウント別汎用設定
   */
  private putAccountGenericSetting(setting: IAccountGenericSetting): Observable<any> {
    return this.accountGenericSettingResource.putByEntity(setting);
  }

  /**
   * アカウント別汎用設定を登録します。
   * @param setting アカウント別汎用設定
   */
  private postAccountGenericSetting(setting: IAccountGenericSetting): Observable<any> {
    return this.getBlUserId().pipe(
      mergeMap(blUserId => {
        setting.blUserId = blUserId;
        return this.accountGenericSettingResource.post(setting);
      })
    );
  }

  /**
   * 受付一覧初期表示設定にparseします。
   * @param accountGenericSetting アカウント別汎用設定
   * @returns 受付一覧初期表示設定
   */
  private parseScreenActionSetting(accountGenericSetting?: IAccountGenericSetting): IReceptSearchScreenActionSetting {
    const baseSetting = _cloneDeep(BaseScreenActionSetting);
    if (accountGenericSetting) {
      try {
        const parsed = <IReceptSearchScreenActionSetting>JSON.parse(accountGenericSetting.settingInfoDataJson);

        // 保存されている値をそのまま使いつつ、未設定の場合のみデフォルトを採用
        let start = parsed.searchDateRangeStartDays;
        let end = parsed.searchDateRangeEndDays;
        let searchTargetDateType = parsed.searchTargetDateType;
        let bizNegoStatusList = parsed.bizNegoStatusList;
        let monthWeekDayDivStart = parsed.monthWeekDayDivStart;
        let monthWeekDayDivEnd = parsed.monthWeekDayDivEnd;

        if (_isNil(start)) {
          start = DEFAULT_SEARCH_DATE_RANGE_START_DAYS;
        }
        if (_isNil(end)) {
          end = DEFAULT_SEARCH_DATE_RANGE_END_DAYS;
        }
        if (_isNil(searchTargetDateType) || typeof searchTargetDateType !== 'string') {
          searchTargetDateType = DEFAULT_SEARCH_TARGET_DATE_TYPE;
        }
        if (!Array.isArray(bizNegoStatusList)) {
          bizNegoStatusList = [];
        } else {
          bizNegoStatusList = bizNegoStatusList.filter(v => typeof v === 'string');
        }
        if (_isNil(monthWeekDayDivStart) || typeof monthWeekDayDivStart !== 'string') {
          monthWeekDayDivStart = DEFAULT_MONTH_WEEK_DAY_DIV_START;
        }
        if (_isNil(monthWeekDayDivEnd) || typeof monthWeekDayDivEnd !== 'string') {
          monthWeekDayDivEnd = DEFAULT_MONTH_WEEK_DAY_DIV_END;
        }

        // 許容範囲（-999〜999）に収める
        start = this.clamp(start, -999, 999);
        end = this.clamp(end, -999, 999);

        // ベース設定に上書きして返却
        return {
          ...baseSetting,
          searchDateRangeStartDays: start,
          searchDateRangeEndDays: end,
          monthWeekDayDivStart,
          monthWeekDayDivEnd,
          searchTargetDateType,
          bizNegoStatusList,
        };
      } catch {
        return baseSetting;
      }
    }

    // サーバに設定が無い場合はundefined
    return undefined;
  }

  /**
   * 値を指定した範囲に収めます。
   */
  private clamp(value: number, min: number, max: number): number {
    if (value < min) {
      return min;
    }
    if (value > max) {
      return max;
    }
    return value;
  }
}




// WEBPACK FOOTER //
// ./src/app/page/recept-list-page/modal/recept-search-screen-action-setting-modal/recept-search-screen-action-setting-modal.service.ts